import { jsx, jsxs } from "react/jsx-runtime";
import { ConciergeSidebarWidget } from "@brandclub/bc-widgets";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
/* empty css                       */
import { IconSidebar, IconCompose, IconHome } from "./ConciergeIcons.js";
const fetchConversations = async (apiBaseUrl) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to fetch conversations");
  return response.json();
};
const weightMap = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick == null ? void 0 : onClick();
        },
        "aria-label": ariaLabel,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full",
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-5", children: icon }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
              style: {
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? "auto" : 0,
                color: resolveColor(state.labelColor),
                fontSize: state.labelFontSize,
                fontWeight: weightMap[state.labelFontWeight] ?? 500
              },
              children: label
            }
          )
        ]
      }
    ),
    !isExpanded && isHovered && /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
        style: {
          backgroundColor: resolveColor(state.tooltipBgColor),
          color: resolveColor(state.tooltipTextColor),
          fontSize: state.tooltipFontSize,
          fontWeight: 600,
          lineHeight: "18px",
          borderRadius: state.tooltipBorderRadius
        },
        children: label
      }
    )
  ] });
};
const LogoWithTooltip = ({ state, icons, resolveColor, isExpanded, brandLogoUrl }) => {
  const [logoHovered, setLogoHovered] = useState(false);
  const brandUrl = `https://${state.brandLinkText}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `tw:flex tw:items-center tw:shrink-0 ${isExpanded ? "tw:px-2.5 tw:pt-3 tw:pb-2" : "tw:mt-3"}`,
      onClick: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
          /* @__PURE__ */ jsx(
            "a",
            {
              href: brandUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              onMouseEnter: () => setLogoHovered(true),
              onMouseLeave: () => setLogoHovered(false),
              className: `tw:flex tw:items-center tw:justify-center tw:rounded tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:no-underline ${isExpanded ? "tw:p-1 tw:shrink-0" : "tw:h-10 tw:px-2.5 tw:w-full"}`,
              "aria-label": state.brandLinkText,
              onClick: (e) => e.stopPropagation(),
              children: icons.logoIconUrl || brandLogoUrl ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: icons.logoIconUrl || brandLogoUrl,
                  alt: "Brand",
                  className: "tw:object-contain",
                  style: isExpanded ? {
                    height: state.expandedLogoMaxHeight,
                    maxWidth: state.expandedLogoMaxWidth
                  } : {
                    height: state.logoIconSize,
                    width: state.logoIconSize
                  }
                }
              ) : /* @__PURE__ */ jsx(IconHome, { size: state.logoIconSize, style: { color: resolveColor(state.sidebarIconColor) } })
            }
          ),
          !isExpanded && logoHovered && /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
              style: {
                backgroundColor: resolveColor(state.tooltipBgColor),
                color: resolveColor(state.tooltipTextColor),
                fontSize: state.tooltipFontSize,
                fontWeight: 600,
                lineHeight: "18px",
                borderRadius: state.tooltipBorderRadius
              },
              children: state.brandLinkText
            }
          )
        ] }),
        isExpanded && /* @__PURE__ */ jsxs(
          "a",
          {
            href: brandUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "tw:ml-1.5 tw:whitespace-nowrap tw:overflow-hidden tw:no-underline",
            style: {
              fontSize: state.brandLinkFontSize,
              fontWeight: weightMap[state.labelFontWeight] ?? 500,
              color: resolveColor(state.brandLinkColor),
              lineHeight: "24px"
            },
            onClick: (e) => e.stopPropagation(),
            children: [
              state.brandLinkPrefix,
              /* @__PURE__ */ jsx("span", { style: { textDecoration: "underline", textDecorationSkipInk: "none" }, children: state.brandLinkText })
            ]
          }
        )
      ]
    }
  );
};
const HistoryItem = ({ conversation, isActive, onSelect, state, resolveColor }) => {
  const [hovered, setHovered] = useState(false);
  return /* @__PURE__ */ jsx(
    "button",
    {
      onClick: () => onSelect(conversation.conversationId),
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      style: {
        width: "100%",
        textAlign: "left",
        padding: `${state.chatItemPaddingY}px ${state.chatItemPaddingX}px`,
        backgroundColor: isActive ? resolveColor(state.chatItemActiveBgColor, "rgba(220,221,220,0.5)") : hovered ? resolveColor(state.chatItemHoverBgColor, "rgba(220,221,220,0.3)") : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        borderRadius: state.chatItemBorderRadius,
        display: "block"
      },
      children: /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            fontSize: state.chatItemFontSize,
            fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
            color: resolveColor(state.chatItemColor),
            lineHeight: "24px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: state.chatItemLineClamp,
            WebkitBoxOrient: "vertical"
          },
          children: conversation.title || conversation.preview
        }
      )
    }
  );
};
const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  currentConversationId,
  userProfile,
  apiBaseUrl = "/api-rewards",
  onSignOutClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle,
  mobileOverlay = false
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = isExpandedProp !== void 0;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;
  const handleToggle = () => {
    if (isControlled) onToggle == null ? void 0 : onToggle();
    else setInternalExpanded((p) => !p);
  };
  const { data, isLoading } = useQuery({
    queryKey: ["conversations", apiBaseUrl],
    queryFn: () => fetchConversations(apiBaseUrl),
    enabled: isExpanded,
    staleTime: 5 * 6e4,
    gcTime: 10 * 6e4
  });
  const getInitials = () => {
    const f = (userProfile == null ? void 0 : userProfile.firstName) || "";
    const l = (userProfile == null ? void 0 : userProfile.lastName) || "";
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    return "";
  };
  const initials = getInitials();
  const isSignedIn = (userProfile == null ? void 0 : userProfile.isSignedIn) ?? false;
  const [avatarHovered, setAvatarHovered] = useState(false);
  return /* @__PURE__ */ jsx(
    ConciergeSidebarWidget,
    {
      widgetId: "concierge-sidebar",
      displayName: "Concierge Sidebar",
      wrapperStyle: {
        width: "auto",
        flexShrink: 0,
        display: "flex",
        height: "100%"
      },
      children: (state, icons, resolveColor) => /* @__PURE__ */ jsxs(
        "aside",
        {
          role: "complementary",
          "aria-expanded": isExpanded,
          onClick: !isExpanded ? handleToggle : void 0,
          className: mobileOverlay ? "tw:flex tw:flex-col tw:shrink-0 tw:h-full" : "tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full",
          style: {
            backgroundColor: resolveColor(isExpanded ? state.expandedBgColor : state.collapsedBgColor),
            width: isExpanded ? state.expandedWidth : state.collapsedWidth,
            transition: "width 0.25s ease-in-out",
            cursor: isExpanded ? void 0 : "pointer",
            borderRight: `1px solid ${resolveColor(state.borderColor)}`
          },
          children: [
            /* @__PURE__ */ jsx(
              LogoWithTooltip,
              {
                state,
                icons,
                resolveColor,
                isExpanded,
                brandLogoUrl
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col", children: [
              isExpanded && /* @__PURE__ */ jsx(
                SidebarButton,
                {
                  icon: /* @__PURE__ */ jsx(
                    IconSidebar,
                    {
                      size: state.sidebarIconSize,
                      style: { color: resolveColor(state.closeIconColor) }
                    }
                  ),
                  label: state.closeSidebarLabel,
                  onClick: handleToggle,
                  isExpanded,
                  ariaLabel: state.closeSidebarLabel,
                  state,
                  resolveColor
                }
              ),
              !isExpanded && /* @__PURE__ */ jsx(
                SidebarButton,
                {
                  icon: /* @__PURE__ */ jsx(
                    IconSidebar,
                    {
                      size: state.sidebarIconSize,
                      style: { color: resolveColor(state.sidebarIconColor) }
                    }
                  ),
                  label: state.openSidebarLabel,
                  onClick: handleToggle,
                  isExpanded,
                  ariaLabel: state.openSidebarLabel,
                  state,
                  resolveColor
                }
              ),
              /* @__PURE__ */ jsx(
                SidebarButton,
                {
                  icon: /* @__PURE__ */ jsx(
                    IconCompose,
                    {
                      size: state.composeIconSize,
                      style: { color: resolveColor(state.composeIconColor) }
                    }
                  ),
                  label: state.newChatLabel,
                  onClick: onComposeClick,
                  isExpanded,
                  ariaLabel: state.newChatLabel,
                  state,
                  resolveColor
                }
              )
            ] }),
            isExpanded ? /* @__PURE__ */ jsxs("div", { className: "sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:mt-6 tw:overflow-hidden", children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "tw:px-2.5 tw:shrink-0",
                  style: {
                    fontSize: state.chatHistoryTitleFontSize,
                    fontWeight: weightMap[state.chatHistoryTitleFontWeight] ?? 500,
                    color: resolveColor(state.chatHistoryTitleColor)
                  },
                  children: state.chatHistoryTitle
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-y-auto tw:mt-2 tw:px-1", children: [
                isLoading && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:justify-center tw:py-4", children: /* @__PURE__ */ jsx(LoadingSpinner, { color: resolveColor(state.chatHistoryTitleColor) }) }),
                !isLoading && (!(data == null ? void 0 : data.conversations) || data.conversations.length === 0) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:text-center tw:py-4",
                    style: {
                      fontSize: state.chatItemFontSize,
                      color: resolveColor(state.chatItemColor, "#999")
                    },
                    children: "No conversations yet"
                  }
                ),
                !isLoading && (data == null ? void 0 : data.conversations) && /* @__PURE__ */ jsx("div", { className: "sidebar-history-list", children: data.conversations.map((c) => /* @__PURE__ */ jsx(
                  HistoryItem,
                  {
                    conversation: c,
                    isActive: c.conversationId === currentConversationId,
                    onSelect: (id) => onConversationSelect == null ? void 0 : onConversationSelect(id),
                    state,
                    resolveColor
                  },
                  c.conversationId
                )) })
              ] })
            ] }) : /* @__PURE__ */ jsx("div", { className: "tw:flex-1" }),
            isSignedIn && /* @__PURE__ */ jsxs("div", { className: "tw:relative", onClick: (e) => e.stopPropagation(), children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  "aria-label": state.signOutLabel,
                  onClick: (e) => {
                    e.stopPropagation();
                    onSignOutClick == null ? void 0 : onSignOutClick();
                  },
                  onMouseEnter: () => setAvatarHovered(true),
                  onMouseLeave: () => setAvatarHovered(false),
                  className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:mb-3 tw:cursor-pointer tw:w-full",
                  children: [
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0",
                        style: {
                          width: state.avatarSize,
                          height: state.avatarSize,
                          backgroundColor: resolveColor(state.avatarBgColor),
                          color: resolveColor(state.avatarTextColor),
                          fontSize: state.avatarFontSize,
                          fontWeight: weightMap[state.avatarFontWeight] ?? 600,
                          lineHeight: "12px"
                        },
                        children: initials
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        className: "tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
                        style: {
                          opacity: isExpanded ? 1 : 0,
                          width: isExpanded ? "auto" : 0,
                          color: resolveColor(state.labelColor),
                          fontSize: state.labelFontSize,
                          fontWeight: weightMap[state.labelFontWeight] ?? 500
                        },
                        children: state.signOutLabel
                      }
                    )
                  ]
                }
              ),
              !isExpanded && avatarHovered && /* @__PURE__ */ jsx(
                "div",
                {
                  className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
                  style: {
                    backgroundColor: resolveColor(state.tooltipBgColor),
                    color: resolveColor(state.tooltipTextColor),
                    fontSize: state.tooltipFontSize,
                    fontWeight: 600,
                    lineHeight: "18px",
                    borderRadius: state.tooltipBorderRadius
                  },
                  children: state.signOutLabel
                }
              )
            ] })
          ]
        }
      )
    }
  );
};
const LoadingSpinner = ({ color }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", style: { animation: "spin 1s linear infinite" }, children: [
  /* @__PURE__ */ jsx("style", { children: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }),
  /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: color, strokeWidth: "3", strokeLinecap: "round", strokeDasharray: "32", strokeDashoffset: "12" })
] });
export {
  ConciergeSidebar
};
//# sourceMappingURL=ConciergeSidebar.js.map
